import { quoteIdentFor } from '../extensions/dbEngines';

/**
 * Grid sort levels, in precedence order.
 *
 * A list rather than a single {col, dir}: "newest first, then by name" is an
 * ordinary thing to want, and the ORDER BY that expresses it is one the grid
 * can build. Empty means unsorted, which is not the same as sorted by nothing —
 * callers use it to choose keyset paging.
 *
 * Sorting is server-side (it goes into ORDER BY), so a level names a real
 * column and nothing here compares values.
 */

// Three columns is where the header badges stop being readable, and past that
// the ordering is rarely what anyone meant. Adding a fourth drops the oldest.
export const MAX_SORT_LEVELS = 3;

/**
 * Levels after clicking `col`.
 *
 * A plain click replaces everything: the common case is "sort by this", and
 * having to clear three stale levels first would be tedious. Shift-click adds
 * a level instead, which is the convention every grid uses.
 *
 * Either way the column itself cycles asc → desc → off, so a third click on a
 * column removes it rather than leaving no way back to unsorted.
 */
export function cycleSort(levels, col, { additive = false } = {}) {
  const list = Array.isArray(levels) ? levels : [];
  const existing = list.find((l) => l.col === col);

  if (!additive) {
    // The asc → desc → off cycle only applies once this column IS the sort.
    // Cycling a column that is merely one level of several would clear the
    // other levels as a side effect of a click that never touched them, so
    // from a multi-level sort a plain click collapses to this column instead.
    const isSoleLevel = list.length === 1 && !!existing;
    if (!isSoleLevel) return [{ col, dir: 'asc' }];
    if (existing.dir === 'asc') return [{ col, dir: 'desc' }];
    return [];
  }

  if (!existing) {
    const next = [...list, { col, dir: 'asc' }];
    return next.length > MAX_SORT_LEVELS ? next.slice(next.length - MAX_SORT_LEVELS) : next;
  }
  if (existing.dir === 'asc') {
    return list.map((l) => (l.col === col ? { col, dir: 'desc' } : l));
  }
  return list.filter((l) => l.col !== col);
}

/** 1-based precedence of `col`, or 0 when it is not a sort level. */
export function sortRank(levels, col) {
  const list = Array.isArray(levels) ? levels : [];
  const i = list.findIndex((l) => l.col === col);
  return i === -1 ? 0 : i + 1;
}

/** 'asc' | 'desc' | null. */
export function sortDir(levels, col) {
  const list = Array.isArray(levels) ? levels : [];
  return list.find((l) => l.col === col)?.dir || null;
}

/**
 * ORDER BY terms for these levels, identifiers quoted for the engine.
 *
 * Returns an array rather than a clause so callers can append their own
 * tiebreakers — the export path has to end on the primary key to keep chunk
 * boundaries meaningful.
 */
export function orderTerms(engine, levels) {
  const list = Array.isArray(levels) ? levels : [];
  return list.map((l) => `${quoteIdentFor(engine, l.col)} ${l.dir === 'desc' ? 'DESC' : 'ASC'}`);
}

/** ` ORDER BY …`, or '' when unsorted — ready to concatenate onto a SELECT. */
export function orderByClause(engine, levels) {
  const terms = orderTerms(engine, levels);
  return terms.length ? ` ORDER BY ${terms.join(', ')}` : '';
}

/**
 * Levels that still name a column in this result.
 *
 * Re-running a tab after the projection changed can leave a level pointing at
 * a column that no longer exists; sending it to the server is a query error
 * rather than a wrong answer, but dropping it silently recovers the tab.
 */
export function liveLevels(levels, columns) {
  const known = new Set(Array.isArray(columns) ? columns : []);
  return (Array.isArray(levels) ? levels : []).filter((l) => known.has(l.col));
}
